import {
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import axios from 'axios'
import { FIVE_MINUTES, queryKeys } from './query-keys'
import {
  AddressDto,
  AddressTypes,
  OrganizationDTO,
  BaseOrganizationDTO,
  CreateAddressRequest,
  UpdateAddressRequest,
  ReportRecordAccessRequest
} from '@/api/generated/model'
import {
  createAddress,
  getAddressMappings as getAddressMappingsRequest,
  updateAddress as updateAddressRequest
} from '@/api/generated/address/address'
import { getAgreementMappingsAddresses as getAgreementMappingsAddressesRequest } from '@/api/generated/agreement-mapping/agreement-mapping'
import {
  getOrganizationAddresses as getOrganizationAddressesRequest,
  getOrganizationPartners
} from '@/api/generated/organization/organization'
import { reportRecordAccess } from '@/api/generated/user-access/user-access'

// Auth token for axios is set in use-auth-context.tsx

export const useCreateAddress = (organizationID?: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: CreateAddressRequest): Promise<AddressDto> => {
      const response = await createAddress(request)
      return response.data
    },
    onSuccess: () => {
      if (organizationID) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.organizations.branches(
            organizationID,
            AddressTypes.Branch
          )
        })
      }
    }
  })
}

export const getAddressMappings = async function (
  addressID: string
): Promise<AddressDto[]> {
  const response = await getAddressMappingsRequest(addressID)
  return response.data
}

export const getAgreementMappingsAddresses = async function (
  agreementID: string,
  addressID: string,
  addressType: AddressTypes
): Promise<AddressDto[]> {
  const response = await getAgreementMappingsAddressesRequest(
    agreementID,
    addressID,
    { addressType: addressType }
  )
  return response.data
}

export const getOrganizationAddresses = async function (
  organizationID: string,
  branchID?: string,
  name?: string,
  addressType?: AddressTypes
): Promise<AddressDto[]> {
  // Empty filters are left out of the query, as before.
  const response = await getOrganizationAddressesRequest(organizationID, {
    branchID: branchID || undefined,
    name: name || undefined,
    addressType: addressType || undefined
  })
  return response.data
}

export const useOrganizationPartners = (
  organizationID?: string
): UseQueryResult<BaseOrganizationDTO[]> => {
  return useQuery({
    queryKey: queryKeys.organizations.partners(organizationID),
    queryFn: async () => {
      const response = await getOrganizationPartners(`${organizationID}`)
      return response.data
    },
    staleTime: FIVE_MINUTES,
    enabled: !!organizationID
  })
}

export const updateAddress = async function (
  request: UpdateAddressRequest
): Promise<AddressDto> {
  const response = await updateAddressRequest(request)
  return response.data
}

// User activities
export const reportAction = async function (
  request: ReportRecordAccessRequest
): Promise<void> {
  const response = await reportRecordAccess(request)
  return response.data
}
